import { useFormik } from "formik";
import { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";
import SignUpValidation from "./validation";
import {
  formattedPhone,
  getConsentInfo,
  getCountryCode,
} from "../../../../helpers/utils";
import { useLoading } from "../../../../context/loadingContext";
import { AuthContext } from "../../../../context/authContext";
import { useToast } from "../../../../hooks/useToast";
import FloatingInputField from "../../../molecules/formControls/inputField/FloatingInputField";
import PhoneInputField from "../../../molecules/formControls/inputField/PhoneInputField";
import PasswordRuleList from "../../../molecules/formControls/validation/PasswordRuleList";
import DefaultButton from "../../../atoms/buttons/DefaultButton";
import ConsentCheckboxInputField from "../../../molecules/formControls/inputField/ConsentCheckboxInputField";
import Text from "../../../molecules/content/Text";
import DefaultLinkButton from "../../../atoms/buttons/DefaultLinkButton";

const SignupForm = () => {
  let countryCode = getCountryCode();
  const { t, i18n } = useTranslation();
  const authContext = useContext(AuthContext);
  const { showToast } = useToast();
  const { setLoading } = useLoading();
  const navigate = useNavigate();
  const location = useLocation();

  const kvkkConsent = getConsentInfo("kvkk");
  const loyaltyConsent = getConsentInfo("loyalty");

  const [formData, setFormData] = useState(
    () => location.state?.formData || {}
  );

  const formik = useFormik({
    initialValues: {
      username: uuid(),
      given_name: "",
      family_name: "",
      email: "",
      phone_number: "",
      password: "",
      content_type: kvkkConsent.typeID.toString(),
      version: kvkkConsent.version,
      content_type_loyalty:
        countryCode == "TR" ? loyaltyConsent.typeID.toString() : "",
      version_loyalty: countryCode == "TR" ? loyaltyConsent.version : "",
      registration_method: "normal_flow",
      kvkk: false,
      //loyalty: false,
    },
    validationSchema: SignUpValidation(t, countryCode),
    onSubmit: async (values) => {
      const data = {
        ...values,
        // Her kayıt denemesinde yeni Cognito username üretilir; state'ten geri yüklenen eski uuid UsernameExistsException'a yol açıyordu
        username: uuid(),
        phone_number: formattedPhone(values.phone_number, countryCode),
        country: countryCode,
        content_type: kvkkConsent.typeID.toString(),
        version: kvkkConsent.version,
        content_type_loyalty: countryCode == "TR" ? loyaltyConsent.typeID.toString() : "",
        version_loyalty: countryCode == "TR" ? loyaltyConsent.version : "",
        registration_method: "normal_flow",
      };
      try {
        setLoading(true);
        const response = await authContext.signUpWithEmail(data);
        if (response) {
          navigate("/email-verification", {
            state: {
              formData: formik.values,
            },
          });
        }
      } catch (err) {
        if (
          err.message ===
          "PreSignUp failed with error username or password is wrong."
        ) {
          showToast("error", t("signUp.usernameOrPasswordWrong"));
        }
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      formik.setValues({ ...formData, password: "" });
    }
  }, [formData]);

  return (
    <form onSubmit={formik.handleSubmit} autoComplete={"false"}>
      <div className="flex flex-col gap-4">
        <Text variant={"h2"} className="flex justify-center text-vestelColor">
          {t("signIn.welcome")}
        </Text>
        <FloatingInputField
          id="given_name"
          name="given_name"
          label={t("signUp.name")}
          required={true}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.given_name}
          formikTouched={formik.touched.given_name}
          error={formik.errors.given_name}
        />
        <FloatingInputField
          id="family_name"
          name="family_name"
          label={t("signUp.surname")}
          required={true}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.family_name}
          formikTouched={formik.touched.family_name}
          error={formik.errors.family_name}
        />
        <FloatingInputField
          id="email"
          name="email"
          label={t("signUp.email")}
          required={true}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.email}
          formikTouched={formik.touched.email}
          error={formik.errors.email}
        />
        <PhoneInputField
          id="phone_number"
          name="phone_number"
          required={true}
          onChange={formik.handleChange}
          countryCode={countryCode}
          onBlur={formik.handleBlur}
          value={formik.values.phone_number}
          formikTouched={formik.touched.phone_number}
          error={formik.errors.phone_number}
        />
        <FloatingInputField
          id="password"
          name="password"
          label={t("signUp.password")}
          required={true}
          type="password"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.password}
          formikTouched={formik.touched.password}
          error={formik.errors.password}
        />
        <PasswordRuleList password={formik.values.password} />
        <ConsentCheckboxInputField
          id="kvkk"
          required={true}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.kvkkPolicy}
          formikTouched={formik.touched.kvkkPolicy}
          error={formik.errors.kvkkPolicy}
          labelItems={[
            {
              text: t("signUp.kvkkPolicy"),
              clickable: i18n.language.toUpperCase() === "EN" ? false : true,
              type: "aydınlatma",
            },
            {
              text: t("signUp.readApprove"),
              clickable: i18n.language.toUpperCase() === "EN" ? true : false,
            },
          ]}
          consentText={kvkkConsent.text}
        />
        {countryCode !== "DE" && (
          <ConsentCheckboxInputField
            id="loyalty"
            required={true}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.loyaltyPolicy}
            formikTouched={formik.touched.loyaltyPolicy}
            error={formik.errors.loyaltyPolicy}
            labelItems={[
              {
                text: t("signUp.loyaltyProgram"),
                clickable: i18n.language.toUpperCase() === "EN" ? false : true,
                type: "sadakat",
              },
              {
                text: t("signUp.loyaltyProgramReadApprove"),
                clickable: i18n.language.toUpperCase() === "EN" ? true : false,
              },
            ]}
            consentText={loyaltyConsent.text}
          />
        )}
        <DefaultButton
          type="submit"
          label={t("signUp.createAccount")}
          disabled={!(formik.isValid & formik.dirty) || formik.isSubmitting}
        />
        <div className="flex justify-center gap-2">
          <Text>{t("signUp.haveAccount")}?</Text>
          <DefaultLinkButton uri={"/sign-in"} label={t("signUp.login")} />
        </div>
      </div>
    </form>
  );
};

export default SignupForm;
